<template>
  <div class="memory-form">
    <slot name="header" v-if="!submitted">
      <div class="memory-form__header">
        <span>The Virtual Birthday Card</span>
        <v-btn class="close" elevation="0" @click="close" icon>
          <v-icon>$close</v-icon>
        </v-btn>
      </div>
    </slot>
    <v-form
      lazy-validation
      v-model="isFormValid"
      ref="form"
      @submit.prevent="submit"
      v-if="!submitted"
    >
      <div class="page-intro">
        What made you laugh, cry, and rage? Share your best CMF memories,
        moments, and disasters!
      </div>

      <br />
      <Uploader
        placeholder-title="Upload photo or video"
        placeholder-desc="Files may not exceed 100mb"
        v-model="form.source"
      />
      <br />

      <v-textarea
        counter="6000"
        maxlength="6000"
        v-model="form.description"
        label="Message"
        outlined
        :rules="[validation.required]"
        validate-on-blur
      ></v-textarea>

      <hr />

      <div class="memory-form__your-info">
        <span class="memory-form__section-label"> Your info </span>
      </div>

      <v-layout :wrap="['xs', 'sm'].includes($size)">
        <v-flex xs12 sm12>
          <v-text-field label="School" v-model="form.school"></v-text-field>
        </v-flex>
        <v-flex class="px-3"></v-flex>
        <v-flex xs12 sm12>
          <v-text-field
            label="How many years you participated"
            v-model="form.years"
          ></v-text-field>
        </v-flex>
      </v-layout>
      <v-layout :wrap="['xs', 'sm'].includes($size)">
        <v-flex xs12 sm12>
          <v-text-field
            label="First Name"
            v-model="form.first_name"
            :rules="[validation.required]"
            validate-on-blur
          ></v-text-field>
        </v-flex>
        <v-flex class="px-3"></v-flex>
        <v-flex xs12 sm12>
          <v-text-field
            label="Last Name"
            v-model="form.last_name"
            :rules="[validation.required]"
            validate-on-blur
          ></v-text-field>
        </v-flex>
      </v-layout>
      <v-layout :wrap="['xs', 'sm'].includes($size)">
        <v-flex xs12 sm12>
          <v-text-field
            label="Email"
            v-model="form.email"
            :rules="[validation.required, validation.email]"
            validate-on-blur
          ></v-text-field>
        </v-flex>
        <v-flex class="pa-3"></v-flex>
        <v-flex xs12 sm12> </v-flex>
      </v-layout>

      <v-checkbox
        class="agree-checkbox"
        v-model="agree"
        label="By submitting your information, you are agreeing to the possibility that your name, likeness, and words may be publicly posted on this site and/or any other official Ideas United social media or digital platforms."
      ></v-checkbox>
      <br />
      <v-btn
        :disabled="uploading"
        class="btn-submit"
        color="cmf_darkgrey"
        elevation="0"
        type="submit"
        >{{ uploading ? `Uploading... ${progress}` : "Submit" }}</v-btn
      >

      <br />
      <br />
      <Recaptcha />
    </v-form>
    <div v-else class="pa-8 text-center">
      <v-btn class="close" @click="close" icon elevation="0">
        <v-icon>$close</v-icon>
      </v-btn>
      <div style="margin: auto">
        <div class="h2">
          That was the sweetest/nicest/most vengeful <br />thing we’ve read!
        </div>
        <p>
          Your submission will be reviewed and posted live to the site. Please
          share this opportunity with your friends and family using the link
          below.
        </p>
      </div>
      <br />
      <label class="memory-form__copy-link">
        <div class="memory-form__copy-link-field">
          <input
            style="width: 300px"
            type="text"
            :value="location"
            ref="copy_input"
            readonly
          />
        </div>
        <v-btn
          class="mr-5"
          color="cmf_darkgrey"
          elevation="0"
          @click="copyToClipboard"
        >
          Copy link
        </v-btn>
      </label>
    </div>
  </div>
</template>

<script>
import formMixin from "@/mixins/form.mixin";
import { copyToClipboard } from "@/utils";

export default {
  name: "MemoryForm",
  extends: formMixin,
  data() {
    return {
      form: {},
      location: window.location.href,
      agree: true,
      progress: 0,
      uploading: false,
      submitted: false
    };
  },
  created() {
    this.reset();
  },
  methods: {
    copyToClipboard() {
      this.$refs.copy_input.select();
      copyToClipboard(this.$refs.copy_input.value);
    },
    reset() {
      this.form = {
        first_name: this.currentUser?.first_name || "",
        last_name: this.currentUser?.last_name || "",
        email: this.currentUser?.email || ""
      };
    },
    async submit() {
      if (!this.validate()) {
        return this.$alert(
          "Form is invalid, please fill all required fields",
          "error"
        );
      }
      if (!this.agree) {
        return this.$alert(
          "You must agree to the processing of personal data",
          "error"
        );
      }
      try {
        const formData = new FormData();
        this.uploading = true;

        this.form.page_slug = "birthday";
        for (const key in this.form) {
          formData.append(`memory[${key}]`, this.form[key]);
        }

        await this.$recaptchaLoaded();
        const recaptcha = await this.$recaptcha("login");
        formData.append("recaptcha", recaptcha);

        await this.$api.Memory.create(formData, {
          onUploadProgress: progress => {
            this.progress = progress;
          }
        });
        this.uploading = false;
        this.progress = 0;
        this.reset();
        this.submitted = true;
      } catch {
        this.uploading = false;
        this.progress = 0;
      }
    },
    close() {
      if (!this.uploading) {
        this.reset();
        this.$emit("close");
        this.submitted = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/styles/variables.scss";

.memory-form {
  position: relative;
  color: $cmf-darkgrey;

  p {
    font-size: 20px;
    padding: 0 10%;
  }

  form {
    padding: 0 120px 50px;
  }

  &__header {
    display: flex;
    align-items: center;
    padding: 80px 120px 25px;
    font-weight: 300;
    font-size: 36px;
    line-height: 45px;
    letter-spacing: -0.02em;
  }

  &__tags::v-deep .tags {
    justify-content: normal;
    margin: 0 -5px;
  }

  &__title {
    color: $primary;
    font-weight: bold;
    font-size: 18px;
    margin-right: 15px;
    display: block;
  }

  &__section-label {
    font-size: 18px;
    line-height: 25px;
    font-weight: 700;
  }

  &__moment-to {
    display: flex;
    align-items: center;

    input {
      margin-left: 15px;
      flex: 1;
      font-size: 14px;
      line-height: 18px;
      border-bottom: 1px solid $primary;
      padding: 5px;
    }
  }

  hr {
    height: 1px;
    background: $primary;
  }

  &__your-info {
    margin: 30px 0 20px;
  }

  &__copy-link {
    .v-btn {
      margin: 20px 10px !important;
    }

    &-field {
      text-align: center;
      border: 1px solid #c4c4c4;
      border-radius: 45px;
      font-family: $body-font-family;
      font-size: 14px;
      color: rgba(0, 0, 0, 0.3);
      display: flex;
      align-items: center;
      padding: 0 20px;
      margin: auto;
      width: 300px;
      height: 36px;
      opacity: 0.3;

      input {
        outline: none !important;
        text-align: center;
      }
    }
  }
}
.v-btn--outlined .v-btn__content .v-icon,
.v-btn--round .v-btn__content .v-icon {
  &::v-deep {
    color: $primary;
  }
}

.btn-submit {
  font-size: 16px !important;
  width: 150px;
}

.agree-checkbox {
  .v-input__slot {
    align-items: flex-start;
  }
  &::v-deep .theme--light.v-label {
    font-size: 20px;
    line-height: 1.3;
    color: $primary;
  }
}

.v-input__slot {
  border-radius: 0;
}

@media screen and (max-width: 960px) {
  .memory-form {
    form {
      padding: 20px;
    }

    &__moment-to {
      display: block;

      &__input {
        display: block;
        margin-top: 10px;
        width: 100%;
      }
    }

    &__title {
      width: 100%;
    }

    &__header {
      padding: 20px;
      > span {
        margin-top: 0.75em;
      }
    }
  }
}
</style>
